import dayjs, { Dayjs } from 'dayjs'

interface DateFormatOptions {
  long?: boolean
  capitalize?: boolean
  clock?: () => Dayjs
  useTimeForToday?: boolean
}

export const buildDateString = (date: string, past: boolean, capitalize = false) => {
  const numberOfDays = daysLeft(date)

  if (numberOfDays === 0) return capitalize ? 'Today' : 'today'
  if (numberOfDays < 7) return `${numberOfDays} ${numberOfDays === 1 ? 'day' : 'days'} ${past ? 'ago' : 'left'}`

  return shortMonthWithYear(date)
}

export const fullTimestampFormat = (inputDatetime: string | number, clock: () => Dayjs = dayjs): string => {
  const datetime = dayjs(inputDatetime)
  const today = clock()

  if (datetime.isSame(today, 'year')) {
    return 'ddd, MMM D, h:mm a'
  }

  return 'ddd, MMM D, YYYY, h:mm a'
}

export const dateFormat = (
  inputDatetime: string | number,
  formatOpts: DateFormatOptions = { capitalize: true },
): string => {
  const opts = { clock: dayjs, useTimeForToday: false, ...formatOpts }
  const datetime = dayjs(inputDatetime)
  const today = opts.clock()
  const { capitalize } = opts

  if (datetime.isSame(today.add(1, 'day'), 'day')) {
    return capitalize ? '[Tomorrow]' : '[tomorrow]'
  }

  if (datetime.isSame(today, 'day')) {
    if (opts.useTimeForToday) {
      return 'h:mm a'
    } else {
      return capitalize ? '[Today]' : '[today]'
    }
  }

  if (datetime.isSame(today.subtract(1, 'day'), 'day')) {
    return capitalize ? '[Yesterday]' : '[yesterday]'
  }

  if (datetime.isSame(today, 'week') && !opts?.long) {
    return 'ddd'
  }

  if (datetime.isSame(today, 'year')) {
    return 'MMM D'
  }

  return 'MMM D, YYYY'
}

export const relativeDateWithTime = (inputDatetime: string) => {
  const datetime = dayjs(inputDatetime)
  const today = dayjs()

  if (datetime.isSame(today, 'day')) {
    return `Today at ${datetime.format('h:mma')}`
  }

  if (datetime.isSame(today.subtract(1, 'day'), 'day')) {
    return `Yesterday at ${datetime.format('h:mma')}`
  }

  if (datetime.isSame(today, 'year')) {
    return `${datetime.format('MMM D')} at ${datetime.format('h:mma')}`
  }

  return `${datetime.format('MMM D, YYYY')} at ${datetime.format('h:mma')}`
}

export const timestampDate = (inputDateTime: string): string => {
  const previousYear = dayjs().year() !== dayjs(inputDateTime).year()

  if (previousYear) return dayjs(inputDateTime).format('MMM D, YYYY, h:mma')
  return dayjs(inputDateTime).format('MMM D, h:mma')
}

export const displayDateTime = (inputDatetime: string, opts?: Pick<DateFormatOptions, 'capitalize'>): string => {
  return dayjs(inputDatetime).format(`${dateFormat(inputDatetime, opts)} h:mma`)
}

export const displayDate = (inputDatetime: string | number, opts: DateFormatOptions = {}): string => {
  return dayjs(inputDatetime).format(dateFormat(inputDatetime, opts))
}

export const displayDateFrom = (inputDatetime: string, opts: DateFormatOptions = { capitalize: true }): string => {
  const datetime = dayjs(inputDatetime)
  const today = dayjs()
  const { capitalize } = opts

  if (datetime.isSame(today.subtract(1, 'day'), 'day')) {
    return capitalize ? 'Yesterday' : 'yesterday'
  }

  if (datetime.isBefore(today.subtract(1, 'day').startOf('day'))) {
    const days = today.diff(datetime, 'day') === 1 ? 'day' : 'days' // pluralize doesn't work with the word 'day'
    return `${today.diff(datetime, 'day')} ${days} ago`
  }

  return displayDate(inputDatetime, opts)
}

export const isBeforeToday = (inputDatetime?: string) => {
  if (!inputDatetime) {
    return false
  }
  const datetime = dayjs(inputDatetime)
  const today = dayjs().startOf('day')
  return datetime.isBefore(today)
}

export const daysLeft = (inputDatetime: string) => {
  const datetime = dayjs(inputDatetime).startOf('day')
  const today = dayjs().startOf('day')
  return Math.abs(today.diff(datetime, 'days'))
}

export const shortMonthWithYear = (inputDatetime: string) => {
  return dayjs(inputDatetime).format('MMM D, YYYY')
}

export const dayOfWeekDate = (inputDatetime: string) => {
  return dayjs(inputDatetime).format('dddd, MMM D')
}

export const daysDiff = (date: string) => {
  const currentDay = dayjs()
  return currentDay.diff(date, 'day')
}

export const isToday = (date: string) => {
  return dayjs(date).startOf('day').isSame(dayjs().startOf('day'))
}

export const isTomorrow = (date: string) => {
  return dayjs(date).startOf('day').isSame(dayjs().add(1, 'day').startOf('day'))
}

export const buildFutureDate = (numDays: number) => {
  return dayjs().add(numDays, 'day').format('MMM D, YYYY')
}

export const formatScheduleTime = (postAt?: Dayjs) => {
  if (!postAt) return 'Not scheduled'
  return postAt.format('MMM DD, YYYY hh:mm A')
}
